/**
 * Tekst dymka dla maskotki rodzica (Tata Sianko / Mama Sianko) na hero
 * ParentDashboard.
 *
 * Styl:
 * - Tata: spokojny, wspierający, „mądry tata", czasem żartobliwy, prosty.
 * - Mama: ciepły, emocjonalny, empatyczny, nadal lekki.
 *
 * Reguły (zachowujemy autentyczność):
 * - Mikrokomunikaty, nie cytaty motywacyjne.
 * - Bez „powinieneś / dobry rodzic robi / musisz" — wsparcie i normalizacja.
 * - Krótkie (max ~60 znaków na linię), naturalne.
 *
 * Wybór tekstu zależy od:
 * - childCount (onboarding gdy 0)
 * - pendingCount (kolejka 0 / mała / duża)
 * - hourOfDay (rano / dzień / wieczór)
 * - daysSinceActivity (jak dawno coś się działo w domu)
 *
 * Rotacja: 4× dziennie (sloty po 6h) + reagująca na liczbę pendingów.
 */

type Gender = "m" | "f" | null | undefined;

type Args = {
  name: string;
  pendingCount: number;
  childCount: number;
  gender: Gender;
  /**
   * Liczba dni od ostatniego approved completion w domu. 0 = dziś,
   * undefined gdy nie wiadomo (np. ledwo co konto).
   */
  daysSinceActivity?: number;
  /** Aktualna godzina (0-23). Default: now. */
  hour?: number;
  /**
   * Promo artykułu z bloga sianko.pl wybrany przez `getNextBlogPost`
   * (rotacja po nieobejrzanych / starszych niż 7 dni). Gdy podane, ma
   * najwyższy priorytet w rotacji speech (przebija pending i no-pending).
   * Wywołujący kontroluje opt-in (`prefs.blogPromos`).
   */
  blogPromo?: { hook: string; url: string };
};

import type { ParentPose } from "@/components/brand/ParentMascot";

/**
 * Tekst dymka + sugerowana poza maskotki + opcjonalny URL (gdy bubble ma być
 * klikalne, np. blog promo). Pose dobierany pod kontekst (rano kawa, kolejka
 * → clipboard, długa cisza → spokojna, etc.).
 */
export type ParentSpeech = {
  text: string;
  pose: ParentPose;
  variant: number;
  url?: string;
};

const dayOfYear = (d: Date): number => {
  const start = new Date(d.getFullYear(), 0, 0);
  const diff = +d - +start;
  return Math.floor(diff / 86400000);
};

const pick = <T>(arr: readonly T[], seed: number): T =>
  arr[Math.abs(seed) % arr.length];

/**
 * Seed zmieniający się 4× dziennie (co 6h) + reagujący na liczbę
 * pendingów. Każda zmiana stanu = inny wariant z puli, bez czekania
 * do kolejnego dnia.
 */
const TIME_SLOTS_PER_DAY = 4;
const timeSlot = (h: number): number => Math.floor(h / 6);

const computeSeed = (hour: number, pendingCount: number): number => {
  const base = dayOfYear(new Date()) * TIME_SLOTS_PER_DAY + timeSlot(hour);
  return base + pendingCount;
};

const polishPlural = (
  n: number,
  one: string,
  few: string,
  many: string,
): string => {
  if (n === 1) return one;
  const last2 = n % 100;
  const last1 = n % 10;
  if (last2 >= 12 && last2 <= 14) return many;
  if (last1 >= 2 && last1 <= 4) return few;
  return many;
};

// ─── Pule tekstów ────────────────────────────────────────────────

const ONBOARDING_DAD = [
  "Dodaj dziecko, żeby zacząć.",
  "Najpierw nowy domownik 👨‍👩‍👧",
  "Zacznijmy od dodania dziecka.",
  "Dobra, zaczynamy od młodego 😎",
  "Najpierw dziecko. Potem legenda.",
  "Dodaj małego człowieka i lecimy 💪",
  "Bez ekipy nie ma misji 🌾",
  "No to kompletujemy skład.",
  "Najpierw zawodnik do drużyny 😄",
  "Dodaj dziecko. Reszta się ogarnie.",
  "Każda przygoda zaczyna się od ekipy.",
  "Najpierw młody. Potem podbijamy świat ✨",
  "No dobra, czas dodać wspólnika 😎",
  "Bez dziecka nawet Sianko się gubi 😄",
  "Najpierw ekipa, potem zadania.",
  "Tu wszystko zaczyna się od rodziny.",
  "Nowy domownik i lecimy 💪",
  "Dodaj dziecko. Reszta przyjdzie sama.",
  "Najpierw mały bohater 😎",
  "Dobra, zaczynamy od podstaw.",
  "Rodzina gotowa do startu?",
  "Jedno kliknięcie i ruszamy.",
] as const;

const ONBOARDING_MOM = [
  "Dodaj dziecko, żeby zacząć 💛",
  "Zacznijmy od nowego domownika ✨",
  "Najpierw nowy domownik — potem reszta.",
  "Dodajmy małego człowieka 💛",
  "Tu wszystko zaczyna się od rodziny ✨",
  "Najpierw nowy domownik, dobrze? 🌾",
  "Zaczynamy razem 💛",
  "Dodaj dziecko i powoli ruszamy ✨",
  "Każda rodzina zaczyna od małych kroków 🌱",
  "Dobrze, że tu jesteś 💛",
  "Najpierw Wasza mała drużyna 😄",
  "Rodzina gotowa na wspólne chwile?",
  "Mały start, duże znaczenie ✨",
] as const;

// 0 pendingów, rano (hour < 11)
const NO_PENDING_MORNING_DAD = [
  "Dzień dobry. Spokojny start ☕",
  "Dzień dobry. Cisza i kawa.",
  "Spokojnie — dzień dopiero się zaczyna.",
  "Cześć. Rodzina się rozkręca 😄",
  "Cisza rano? Podejrzane 😄",
  "Jeszcze nikt niczego nie wysadził 💪",
  "Dzień dopiero odpala silnik ☕",
  "Poranek bez kolejki. Luksus.",
  "Na razie pełen spokój 🌾",
  "Może nawet kawa będzie gorąca 😎",
  "Dobra, spokojny start.",
  "Rodzina się jeszcze ładuje 🔋",
  "Póki co — kontrolowany chaos 😄",
  "Cisza przed… śniadaniem.",
  "Jeszcze wszyscy się budzą 😄",
  "Poranki mają swoje tempo ☕",
  "Dzień dopiero się rozkręca.",
  "Cisza rano? Rzadkie i piękne 😎",
  "Może nawet kawa będzie ciepła ☕",
  "Spokojny poranek to luksus 😄",
  "Jeszcze nic nie wybuchło. Jest dobrze.",
  "Rano wszystko wygląda łatwiej ✨",
] as const;

const NO_PENDING_MORNING_MOM = [
  "Dzień dobry ☀️ Powolny start to też dobry start.",
  "Dzień dobry 💛 Najpierw kawa?",
  "Spokojny początek dnia.",
  "Dzień dobry. Daj sobie chwilę.",
  "Dzień dobry 💛 Powoli też jest dobrze.",
  "Może najpierw kawa? ☕",
  "Spokojnego początku dnia ✨",
  "Rano nie trzeba się spieszyć 🌱",
  "Dziś jest wyjątkowo cicho 😄",
  "Mała chwila dla siebie 💛",
  "Dobry poranek zaczyna się spokojnie.",
  "Wdech, kawa i lecimy ☀️",
  "Rodzina dopiero się budzi ✨",
  "Spokojny start to też dobry start 💛",
] as const;

// 0 pendingów, wieczór (hour >= 18)
const NO_PENDING_EVENING_DAD = [
  "Dobry wieczór. Dzieci dziś dały radę 💪",
  "Wieczór bez kolejki — gratulacje.",
  "Wszyscy żywi. Mały sukces 😄",
  "Konsekwencja wygrywa z perfekcją.",
  "Wieczór bez zaległości to sukces 😎",
  "Dobra robota dziś, serio 💪",
  "Można zwolnić tempo 🌙",
  "Nie perfekcyjnie. Wystarczająco dobrze.",
  "Dziś rodzina dała radę 🌾",
  "Wieczór bez gaszenia pożarów ☕",
  "Małe kroki codziennie. To działa.",
  "Dobra, dziś już wystarczy 😄",
  "Cisza po 20:00 robi swoje 🌙",
  "Drzewo rośnie powoli — i to dobrze 🌳",
  "Nuda też uczy — daj jej miejsce 🤔",
  "Wieczór bez zaległości brzmi dobrze ☕",
  "Dziś wystarczyło. Naprawdę.",
  "Koniec dnia bez pożaru 😄",
  "Dobra robota całej ekipie 🌾",
  "Małe kroki codziennie 💪",
  "Wieczorny spokój wygrywa.",
  "Nikt nie musi być idealny.",
  "Czas zwolnić tempo 🌙",
  "Takie zwykłe dni są ważne.",
  "Dziś po prostu było okej 💛",
] as const;

const NO_PENDING_EVENING_MOM = [
  "Dobry wieczór 🌙 Dziś była mała wygrana.",
  "Codzienne rytuały robią swoje.",
  "Dziś też wystarczyło. Naprawdę 💛",
  "Spokojny wieczór. Tego było potrzeba.",
  "Dobry wieczór 💛 Dziś wystarczyło.",
  "Wieczór bez chaosu brzmi pięknie 🌙",
  "Można trochę zwolnić ✨",
  "Dobra robota na dziś 💛",
  "Spokojny dom to mała wygrana 🌾",
  'Dziś było dużo małych „udało się" 😄',
  "Nie perfekcyjnie. Po ludzku 💛",
  "Czas odpocząć, serio ✨",
  "Wieczór też potrzebuje ciszy 🌙",
  "Dziś po prostu było dobrze 💛",
  "Cisza po 20:00 to dar dla wszystkich 🌙",
  "Spokojny wieczór = lepsza pamięć dziecka 💛",
  "Drzewo rośnie powoli — i ty też 🌳",
] as const;

// 0 pendingów, w ciągu dnia
const NO_PENDING_DAY_DAD = [
  "Wszystko zatwierdzone ✨",
  "Małe rzeczy regularnie — wielkie nawyki 🌾",
  "Jedno przypomnienie mniej to też sukces 😄",
  "Cisza w skrzynce. Pewnie dobrze.",
  "Spokój. Możesz odpocząć ☕",
  "Tak między nami — nawet Sianko nie chce dziś nic 😎",
  "Wszystko ogarnięte. Szanuję 💪",
  "Dziś system działa jak trzeba 😎",
  "Mały porządek, spokojniejsza głowa.",
  "Dobra robota całej ekipie 🌾",
  "Panel pusty. Piękny widok 😄",
  "Dziś nawet Sianko odpoczywa ☕",
  "Zero zaległości. Brzmi profesjonalnie.",
  "Nic nie czeka. Można żyć ✨",
  "Takie dni robią robotę.",
  "Spokojnie. Jest dobrze.",
  "Dziś system działa bez hałasu 😄",
  "Wygląda na ogarnięty dzień 🌾",
  "Spokojny panel = spokojniejsza głowa.",
  "Dobra robota po obu stronach 💪",
  "Nic nie czeka. Piękna sprawa.",
  "Takie dni lubimy ☕",
  "Mały porządek robi dużą różnicę.",
  "Bez zaległości. Szanuję 😎",
  "Dzieci ogarnęły temat ✨",
  "Można chwilę odetchnąć.",
] as const;

const NO_PENDING_DAY_MOM = [
  "Wszystko zatwierdzone ✨",
  "Spokojny moment 💛 Odetchnij.",
  "Codzienne rytuały budują bezpieczeństwo.",
  'Każde małe „udało się" się liczy.',
  "Brak presji — dom też potrzebuje oddechu.",
  "Wszystko dziś płynie spokojnie 💛",
  "Mały oddech dla głowy ✨",
  "Codzienność też zasługuje na uznanie 🌾",
  "To był dobry, zwykły dzień 😄",
  "Spokój w domu robi różnicę.",
  "Dziś można trochę odetchnąć 💛",
  "Małe rytuały budują bezpieczeństwo ✨",
  "Jest spokojnie. I dobrze.",
  "Dzieci dziś naprawdę się starały 🌱",
  "Czasem właśnie takie dni są najlepsze 💛",
] as const;

// Są pendingi — wariacje z imieniem i liczbą
const PENDING_DAD = [
  (n: string, k: number, w: string, v: string) =>
    `${n ? `Cześć ${n}. ` : ""}${k} ${w} ${v}.`,
  (n: string, k: number, w: string) =>
    `${k} ${w} do rzutu okiem${n ? `, ${n}` : ""}.`,
  (n: string, k: number, w: string) =>
    `${k} ${w}. Lecimy?${n ? ` Hej ${n}.` : ""}`,
  (_n: string, k: number, w: string) =>
    `Małe sprawy: ${k} ${w} czeka.`,
  (n: string, _k: number, _w: string) =>
    `${n ? `Hej ${n}, ` : "Hej, "}coś tam się uzbierało 😄`,
  (_n: string, k: number, w: string) =>
    `${k} ${w} czeka na Twoje oko.`,
  () => "Młodzi dziś działali 💪",
  () => "Kilka rzeczy do klepnięcia 😎",
  () => "Panel woła delikatnie 🌾",
  () => "No dobra, ktoś był produktywny 😄",
  (_n: string, k: number, w: string) =>
    `${k} ${w}. Szybka akcja i z głowy.`,
  () => "Dzieci nie próżnowały 💪",
  () => "Trochę roboty, ale tej dobrej.",
  () => "Ktoś dziś poszedł na rekord 😎",
  (n: string, _k: number, _w: string) =>
    `${n ? `Hej ${n}, ` : "Hej, "}coś tam się nazbierało 😄`,
  (_n: string, k: number, w: string) =>
    `${k} ${w} czeka spokojnie.`,
  () => "Dzieci działają pełną parą 💪",
  () => "Rzut oka i będzie z głowy.",
  () => "Kilka małych rzeczy do sprawdzenia.",
  (_n: string, k: number, w: string) =>
    `${k} ${w}. Bez pośpiechu.`,
  () => "Ktoś dziś działał konkretnie 🌾",
  () => "Sianko melduje aktywność 😄",
] as const;

const PENDING_MOM = [
  (n: string, k: number, w: string, v: string) =>
    `${n ? `Cześć ${n} 💛 ` : ""}${k} ${w} ${v}.`,
  (n: string, k: number, w: string) =>
    `${n ? `Hej ${n}, mam ${k} ${w} dla Ciebie.` : `${k} ${w} dla Ciebie.`}`,
  (_n: string, k: number, w: string) =>
    `${k} ${w} do zatwierdzenia 💛`,
  (_n: string, k: number, w: string) =>
    `Dzieci się starały — ${k} ${w}.`,
  (n: string, _k: number, _w: string) =>
    `${n ? `Hej ${n} 💛 ` : ""}Dzieci dziś działały.`,
  (_n: string, k: number, w: string) =>
    `${k} ${w} czeka spokojnie ✨`,
  () => "Ktoś się dziś naprawdę postarał 🌾",
  () => "Kilka małych sukcesów do zobaczenia 💛",
  () => "Dzieci zostawiły trochę dobrej energii 😄",
  () => "Rzut oka i gotowe ✨",
  (_n: string, k: number, w: string) =>
    `${k} ${w}. Bez pośpiechu 💛`,
  () => "Małe rzeczy, wielka nauka 🌱",
  () => "To były ważne małe kroki ✨",
  () => "Dzieci dziś próbowały. To się liczy 💛",
] as const;

// Dużo pendingów (≥5) — dorzucamy lekkie żartobliwe
const PENDING_MANY_EXTRA_DAD = [
  (_n: string, k: number, w: string) =>
    `${k} ${w} — wzięli się do roboty 💪`,
  (_n: string, k: number, w: string) =>
    `Dużo się dzieje: ${k} ${w}.`,
  () => "No, dziś młodzi odpalili turbo 💪",
  () => "Dużo się działo. Szanuję 😄",
  (_n: string, k: number, w: string) =>
    `${k} ${w}. Ktoś tu ostro działał.`,
  () => "Panel trochę płonie 🔥",
  () => "Dzieci dziś weszły na pełnych obrotach 🌾",
  () => "Ależ oni dziś cisnęli 😎",
  (n: string) => `Kolejka konkretna${n ? `, ${n}` : ""} 💪`,
  () => "Dobra wiadomość? Chcieli działać 😄",
  () => "Trochę tego jest, ale to dobry znak.",
  () => "No dobra. Produktywny dzień.",
  () => "Ale dziś był ruch 💪",
  (n: string) => `Solidna kolejka${n ? `, ${n}` : ""} 😄`,
  () => "Wygląda na produktywny dzień 🌾",
  () => "Ktoś dziś naprawdę działał.",
  () => "Dużo małych sukcesów ✨",
  () => "Panel trochę żyje 😎",
  () => "Dzieci się rozpędziły 💛",
  () => "Pełne ręce roboty 😄",
  () => "No dobra, dziś poszli grubo 💪",
] as const;

const PENDING_MANY_EXTRA_MOM = [
  (n: string, k: number, w: string) =>
    `${n ? `${n}, ` : ""}dzieci się starały — ${k} ${w} 💛`,
  (_n: string, k: number, w: string) =>
    `${k} ${w}. Małe „udało się" budują pewność.`,
  () => "Oj 💛 Dziś było pracowicie.",
  () => "Dzieci naprawdę się starały ✨",
  () => `Tyle małych „udało się" dziś 🌾`,
  () => "Dużo aktywności, dużo prób 💛",
  () => "To był intensywny dzień 😄",
  () => "Panel pełen dziecięcej energii ✨",
  () => "Naprawdę dużo się wydarzyło 🌱",
  () => "Trochę tego jest, ale w dobrym sensie 💛",
  () => "Dzieci dziś dały z siebie sporo.",
  () => "Dużo małych kroków robi dużą różnicę ✨",
] as const;

// Blog promo — dymek pokazuje „zaczepkę" artykułu (pytanie / teaser).
// Lambda przyjmuje `hook` z `BlogPost`. CTA „Przeczytaj na blogu →"
// dokłada UI bubble (Dashboards), więc tekst to sama zaczepka — bez
// „świeży wpis", bo artykuły rotują (getNextBlogPost), nie są nowe.
const BLOG_PROMO_DAD = [
  (h: string) => h,
  (h: string) => `📖 ${h}`,
  (h: string) => `Hej — ${h}`,
] as const;

const BLOG_PROMO_MOM = [
  (h: string) => `${h} 💛`,
  (h: string) => `📖 ${h}`,
  (h: string) => `${h} ✨`,
] as const;

// Długa przerwa (≥5 dni bez aktywności) — łagodne, bez presji
const INACTIVE_DAD = [
  "Spokojnie. Każda rodzina łapie wolniejsze tempo 😊",
  "Bez presji. Dobre nawyki wracają.",
  "Nie wszystko od razu — to też lekcja.",
  "Spokojnie. Każdy czasem odpuszcza.",
  "Życie bywa szybsze niż plan 😄",
  "Nie trzeba robić wszystkiego idealnie.",
  "Wracamy od jednej małej rzeczy 💪",
  "Kilka cichszych dni? Normalna sprawa.",
  "Bez spiny. To nie wyścig.",
  "Rodzina to maraton, nie sprint 🌾",
  "Czasem sam fakt, że działacie razem, wystarcza.",
  "Nawyki buduje się długo. I dobrze.",
  "Nie ma punktów za perfekcję 😎",
  "Czasem życie wygrywa z planem.",
  "To ma pomagać, nie męczyć 💛",
  "Każda rodzina ma swoje tempo.",
  "Spokojnie. Wraca się od małych rzeczy.",
  "Nie trzeba perfekcji 🌾",
  "Najważniejsze, że wracacie.",
  "Czasem odpoczynek też jest produktywny ☕",
] as const;

const INACTIVE_MOM = [
  "Spokojnie 💛 Każda rodzina łapie wolniejsze tempo.",
  "Brak presji. Wracamy w swoim tempie.",
  "To normalne, że czasem brakuje cierpliwości.",
  "Spokojnie 💛 Każda rodzina ma trudniejsze dni.",
  "Nie trzeba być idealnym.",
  "Czasem samo przetrwanie dnia wystarcza ✨",
  "Wracamy wtedy, kiedy jesteście gotowi 🌱",
  "To ma pomagać, nie obciążać 💛",
  "Kilka cichszych dni to normalna rzecz.",
  "Daj sobie trochę łagodności ✨",
  "Dobre nawyki wracają małymi krokami 🌾",
  "Nie wszystko trzeba zrobić od razu.",
  "Jesteście okej 💛",
] as const;

// ─── Główna funkcja ──────────────────────────────────────────────

export function parentMascotSpeech({
  name,
  pendingCount,
  childCount,
  gender,
  daysSinceActivity,
  hour,
  blogPromo,
}: Args): ParentSpeech {
  const isMom = gender === "f";
  const h = hour ?? new Date().getHours();
  const seed = computeSeed(h, pendingCount);

  // 0 dzieci — onboarding (tu blog promo nie ma sensu, user dopiero zaczyna)
  if (childCount === 0) {
    return {
      text: pick(isMom ? ONBOARDING_MOM : ONBOARDING_DAD, seed),
      pose: "finger-down",
      variant: seed,
    };
  }

  // Blog promo — najwyższy priorytet wśród zwykłych stanów (przebija
  // pending/no-pending). Wywołujący już wybrał kandydata przez
  // `getNextBlogPost` — my tylko losujemy wariant tekstu.
  if (blogPromo) {
    const fn = pick(isMom ? BLOG_PROMO_MOM : BLOG_PROMO_DAD, seed);
    return {
      text: fn(blogPromo.hook),
      pose: "info",
      variant: seed,
      url: blogPromo.url,
    };
  }

  // Długa przerwa (≥5 dni bez aktywności w domu) — łagodne, niezależnie
  // od pendingów. Daje rodzicowi sygnał „bez stresu".
  if (daysSinceActivity != null && daysSinceActivity >= 5) {
    return {
      text: pick(isMom ? INACTIVE_MOM : INACTIVE_DAD, seed),
      pose: "calm",
      variant: seed,
    };
  }

  // 0 pendingów — kontekst pory dnia
  if (pendingCount === 0) {
    if (h < 11) {
      return {
        text: pick(
          isMom ? NO_PENDING_MORNING_MOM : NO_PENDING_MORNING_DAD,
          seed,
        ),
        pose: "coffee",
        variant: seed,
      };
    }
    if (h >= 18) {
      return {
        text: pick(
          isMom ? NO_PENDING_EVENING_MOM : NO_PENDING_EVENING_DAD,
          seed,
        ),
        pose: "relaxed",
        variant: seed,
      };
    }
    return {
      text: pick(isMom ? NO_PENDING_DAY_MOM : NO_PENDING_DAY_DAD, seed),
      // Dzień bez kolejki — mama z dłońmi przy sercu („dzieci się starały"),
      // tata bez dedykowanej pozy → fallback na default.
      pose: isMom ? "heart" : "default",
      variant: seed,
    };
  }

  // Są pendingi
  const word = polishPlural(
    pendingCount,
    "zgłoszenie",
    "zgłoszenia",
    "zgłoszeń",
  );
  const verb = polishPlural(pendingCount, "czeka", "czekają", "czeka");
  const pool = isMom ? PENDING_MOM : PENDING_DAD;
  const extras = pendingCount >= 5
    ? (isMom ? PENDING_MANY_EXTRA_MOM : PENDING_MANY_EXTRA_DAD)
    : [];
  const all = [...pool, ...extras];
  const fn = pick(all, seed);
  // ≥5 zgłoszeń → tata żartobliwie przytłoczony, mama dumna „dzieci dały radę".
  // Mała kolejka → tata z clipboardem, mama bez dedykowanej pozy → default.
  const pose: ParentPose = pendingCount >= 5
    ? "overwhelmed"
    : isMom
      ? "default"
      : "check";
  return {
    text: fn(name, pendingCount, word, verb),
    pose,
    variant: seed,
  };
}
